import React, { useEffect, useMemo, useRef, useState } from 'react';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import { KendoReactTextArea } from '@Shared.Angular/kendo.react/KendoReactTextArea';
import { FileUpload } from '@Shared.Angular/flowingly.components/FileUpload/FileUpload';
import { FileFolder } from '@Shared.Angular/flowingly.services/file.service';

export interface CreationHubFlowModelDetails {
  name: string;
  description: string;
  categoryId?: Guid;
  objective: string;
  background: string;
  processInput: string;
  processOutput: string;
  triggerInput: string;
  generationFileId?: Guid;
}

export enum FlowModelDetailsPanelMode {
  FROM_SCRATCH,
  FROM_UPLOAD,
  FROM_RECORDING
}

type CreationHubFlowModelDetailsPanelProps = {
  mode: FlowModelDetailsPanelMode;
  uploadAcceptTypes?: string[];
  details: CreationHubFlowModelDetails;
  onDetailsChange: (model: CreationHubFlowModelDetails) => void;
  isDisabled?: boolean;
  showNameExistsError?: boolean;
  onValidationChange?: (validationError: string) => void;
  onUploadPendingChange?: (isPending: boolean) => void;
};

export const CreationHubFlowModelDetailsPanel = ({
  mode,
  uploadAcceptTypes,
  details,
  onDetailsChange,
  isDisabled = false,
  showNameExistsError = false,
  onValidationChange,
  onUploadPendingChange
}: CreationHubFlowModelDetailsPanelProps) => {
  const [categories, setCategories] = useState<ICategoryDetail[]>([]);
  const flowModelDetailsRef = useRef(details);
  flowModelDetailsRef.current = details;

  useEffect(() => {
    getService('categoryApiService')
      .getCachedCategories()
      .then((categoryDetails) => {
        setCategories(categoryDetails);
      });
  }, []);

  const APP_CONFIG = getService('APP_CONFIG');
  const fieldMaxLength = 4000;

  const nameXssValidation = useMemo(
    () => XssValidation(details.name),
    [details.name]
  );
  const descriptionXssValidation = useMemo(
    () => XssValidation(details.description),
    [details.description]
  );
  const objectiveXssValidation = useMemo(
    () => XssValidation(details.objective),
    [details.objective]
  );
  const backgroundXssValidation = useMemo(
    () => XssValidation(details.background),
    [details.background]
  );
  const inputXssValidation = useMemo(
    () => XssValidation(details.processInput),
    [details.processInput]
  );
  const outputXssValidation = useMemo(
    () => XssValidation(details.processOutput),
    [details.processOutput]
  );
  const triggerXssValidation = useMemo(
    () => XssValidation(details.triggerInput),
    [details.triggerInput]
  );

  const xssErrorExists =
    nameXssValidation ||
    descriptionXssValidation ||
    objectiveXssValidation ||
    backgroundXssValidation ||
    inputXssValidation ||
    outputXssValidation ||
    triggerXssValidation;

  const validationError =
    !details.name || details.name.trim() === ''
      ? 'Name is required'
      : APP_CONFIG.enableGovernance && !details.categoryId
      ? 'Category is required'
      : xssErrorExists
      ? 'Please fix validation errors'
      : '';

  useEffect(() => {
    onValidationChange?.(validationError);
  }, [validationError]);

  function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>(null);
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const previousDetails = usePrevious(details);

  return (
    <div className="creation-hub-flow-model-details-panel flowingly-form">
      {showNameExistsError && (
        <div className="validation-errors">
          <p className="center-align">
            There is already a Flow Model with this name. Please enter a unique
            name.
          </p>
        </div>
      )}
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-name">Name *</label>
        <input
          value={details.name}
          onChange={(e) =>
            onDetailsChange({ ...details, name: e.target.value })
          }
          name="flow-model-details-name"
          id="flow-model-details-name"
          type="text"
          required
          autoComplete="off"
          disabled={isDisabled}
        />
        {nameXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-description">Description</label>
        <KendoReactTextArea
          placeholder="Short summary"
          value={details.description}
          onChange={(e) =>
            onDetailsChange({ ...details, description: e.target.value })
          }
          id="flow-model-details-description"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {descriptionXssValidation}
      </div>
      {APP_CONFIG.enableGovernance && (
        <div className="flowingly-field">
          <label htmlFor="flow-model-details-category-dropdown">
            Category *
          </label>
          <KendoReactDropDownTree
            id="flow-model-details-category-dropdown"
            categories={categories}
            categoryClicked={(
              categoryId: Guid,
              index,
              name,
              category: ICategoryDetail
            ) => {
              onDetailsChange({
                ...details,
                categoryId: categoryId
              });
            }}
            placeholder="Select a category"
            selectedId={details.categoryId}
            dataItemKey="id"
            textField="name"
            subItemsField="subCategories"
            defaultItemId={null}
            showFilter={true}
            filterThreshold={8}
            disabled={isDisabled}
          />
        </div>
      )}
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-objective">Objective</label>
        <KendoReactTextArea
          placeholder="Goal or outcome"
          value={details.objective}
          onChange={(e) =>
            onDetailsChange({
              ...details,
              objective: e.target.value
            })
          }
          id="flow-model-details-objective"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {CharacterLimitWarning(
          previousDetails?.objective,
          details.objective,
          fieldMaxLength
        )}
        {objectiveXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-background">Background</label>
        <KendoReactTextArea
          placeholder="Context or background"
          value={details.background}
          onChange={(e) =>
            onDetailsChange({
              ...details,
              background: e.target.value
            })
          }
          id="flow-model-details-background"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {CharacterLimitWarning(
          previousDetails?.background,
          details.background,
          fieldMaxLength
        )}
        {backgroundXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-process-input">Process Input</label>
        <KendoReactTextArea
          placeholder="What goes into the process"
          value={details.processInput}
          onChange={(e) =>
            onDetailsChange({
              ...details,
              processInput: e.target.value
            })
          }
          id="flow-model-details-process-input"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {CharacterLimitWarning(
          previousDetails?.processInput,
          details.processInput,
          fieldMaxLength
        )}
        {inputXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-process-output">
          Process Output
        </label>
        <KendoReactTextArea
          placeholder="What the process produces"
          value={details.processOutput}
          onChange={(e) =>
            onDetailsChange({
              ...details,
              processOutput: e.target.value
            })
          }
          id="flow-model-details-process-output"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {CharacterLimitWarning(
          previousDetails?.processOutput,
          details.processOutput,
          fieldMaxLength
        )}
        {outputXssValidation}
      </div>
      <div className="flowingly-field">
        <label htmlFor="flow-model-details-trigger-input">Trigger</label>
        <KendoReactTextArea
          placeholder="What initiates the process"
          value={details.triggerInput}
          onChange={(e) =>
            onDetailsChange({
              ...details,
              triggerInput: e.target.value
            })
          }
          id="flow-model-details-trigger-input"
          autoSize={true}
          rows={3}
          disabled={isDisabled}
        />
        {CharacterLimitWarning(
          previousDetails?.triggerInput,
          details.triggerInput,
          fieldMaxLength
        )}
        {triggerXssValidation}
      </div>
      {mode === FlowModelDetailsPanelMode.FROM_UPLOAD && (
        <div className="flowingly-field">
          <label htmlFor="flow-model-details-generation-file">
            Upload File
          </label>
          <FileUpload
            uploadFolder={FileFolder.FlowModelGeneration}
            acceptTypes={uploadAcceptTypes || []}
            isDisabled={isDisabled}
            onUploadStart={() => {
              onUploadPendingChange && onUploadPendingChange(true);
            }}
            onUploadComplete={() => {
              onUploadPendingChange && onUploadPendingChange(false);
            }}
            onUpload={(fileId) => {
              onDetailsChange({
                ...flowModelDetailsRef.current,
                generationFileId: fileId
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

const XssValidation = (value: string) => {
  if (getService('validationService').isXssVulnerableString(value)) {
    return <div className="error-text">This input does not support HTML</div>;
  }
  return undefined;
};

const CharacterLimitWarning = (
  oldValue: string,
  newValue: string,
  limit: number
) => {
  if (oldValue === newValue) {
    return <></>;
  }
  return (
    <div className="secondary-text">
      Number of characters left: {limit - (newValue?.length || 0)}
    </div>
  );
};
